import React from "react";
import { useNavigate } from "react-router-dom";
import { startCommunication } from "../utils/callHandler";

export default function RequestList({ requests, handleAction, seatsFull }) {
  const navigate = useNavigate();

  if (!requests || requests.length === 0) {
    return (
      <div style={styles.emptyState}>
        <span style={{ fontSize: "40px", marginBottom: "10px" }}>📭</span>
        <p>No requests found for this ride.</p>
      </div>
    );
  }

  const getInitials = (name) => {
    return name ? name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2) : "??";
  };

  return (
    <>
      <style>
        {`
          .req-card:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(0,0,0,0.08) !important; }
          .req-btn:hover { filter: brightness(0.95); transform: scale(1.02); }
          .req-btn:active { transform: scale(0.98); }
          
          @media (max-width: 480px) {
            .req-card { flex-direction: column; align-items: flex-start; gap: 15px; }
            .req-actions { width: 100%; justify-content: stretch; }
            .req-btn { flex: 1; justify-content: center; }
            .req-info-row { width: 100%; }
          }
        `}
      </style>

      <div style={styles.container}>
        {requests.map((req) => {
          // SAFE DATA EXTRACTION
          const passenger = req.passenger || req.user || {};
          const pId = passenger._id || passenger.id;
          const avatarUrl = passenger.avatar;
          const status = String(req.status || "pending").toLowerCase();

          return (
            <div key={req._id} className="req-card" style={{
              ...styles.card,
              backgroundColor: status === 'cancelled' ? '#fff1f2' : '#ffffff' 
            }}>
              
              <div className="req-info-row" style={styles.leftSection}>
                <div style={styles.avatarContainer}>
                  {avatarUrl ? (
                    <img src={avatarUrl} alt="user" style={styles.avatarImg} />
                  ) : (
                    <div style={styles.avatarPlaceholder}>{getInitials(passenger.name)}</div>
                  )}
                </div>
                
                <div style={styles.userInfo}>
                  <h4 style={styles.userName}>{passenger.name || "Unknown User"}</h4>
                  <div style={styles.metaBadge}>
                    <span style={styles.icon}>💺</span>
                    <span><strong>{req.seatsRequested || req.seats || 1}</strong> seat(s) requested</span>
                  </div>

                  {/* ✅ THE FIX: DISPLAY THE FLEXIBLE ROUTE IF IT EXISTS! */}
                  {req.flexibleRoute && (
                    <div style={styles.flexibleBadge}>
                      <span style={styles.icon}>⚠️</span>
                      <span>Flexible: <strong>{req.flexibleRoute}</strong></span>
                    </div>
                  )}
                </div>
              </div>

              {/* SMART ACTION ENGINE */}
              <div className="req-actions" style={styles.actionGroup}>
                
                {/* 1. PASSENGER CANCELLED */}
                {status === "cancelled" && (
                  <div style={{ ...styles.statusLabel, ...styles.cancelledLabel }}>
                    🚫 Booking Cancelled by Passenger
                  </div>
                )}

                {/* 2. DRIVER REJECTED */}
                {status === "rejected" && (
                  <div style={{ ...styles.statusLabel, ...styles.rejectedLabel }}>
                    Declined
                  </div>
                )}

                {/* 3. PENDING (SHOW ACCEPT/REJECT) */}
                {status === "pending" && (
                  <>
                    <button
                      className="req-btn"
                      style={{ ...styles.btn, ...styles.acceptBtn, opacity: seatsFull ? 0.6 : 1 }}
                      onClick={() => handleAction(req._id, "accept")}
                      disabled={seatsFull}
                    >
                      {seatsFull ? "Full" : "✔ Accept"}
                    </button>

                    <button
                      className="req-btn"
                      style={{ ...styles.btn, ...styles.rejectBtn }}
                      onClick={() => handleAction(req._id, "reject")}
                    >
                      ✖ Reject
                    </button>
                  </>
                )}

                {/* 4. ACCEPTED/CONFIRMED (SHOW CHAT/CALL) */}
                {(status === "accepted" || status === "confirmed") && (
                  <div style={styles.commWrapper}>
                    <button
                      className="req-btn"
                      style={{ ...styles.btn, ...styles.chatBtn }}
                      onClick={() => startCommunication(navigate, pId, "chat")}
                    >
                      💬 Chat
                    </button>
                    <button
                      className="req-btn"
                      style={{ ...styles.btn, ...styles.callBtn }}
                      onClick={() => startCommunication(navigate, pId, "audio")}
                    >
                      📞 Call
                    </button>
                  </div>
                )}
              </div>

            </div>
          );
        })}
      </div>
    </>
  );
}

const styles = {
  container: { display: "flex", flexDirection: "column", gap: "15px", width: "100%", marginTop: "10px" },
  emptyState: { textAlign: "center", padding: "40px 20px", color: "#999", background: "#f9f9f9", borderRadius: "12px", border: "2px dashed #eee" },
  card: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px", borderRadius: "16px", transition: "all 0.2s", border: "1px solid #f0f0f0", flexWrap: "wrap" },
  leftSection: { display: "flex", alignItems: "center", gap: "12px" },
  avatarPlaceholder: { width: "48px", height: "48px", borderRadius: "50%", backgroundColor: "#276ef1", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "16px", fontWeight: "700" },
  avatarImg: { width: "48px", height: "48px", borderRadius: "50%", objectFit: "cover" },
  userInfo: { display: "flex", flexDirection: "column", gap: "4px" },
  userName: { margin: 0, fontSize: "15px", fontWeight: "700" },
  metaBadge: { fontSize: "12px", color: "#666", backgroundColor: "#f3f3f3", padding: "3px 8px", borderRadius: "8px", display: "flex", gap: "4px", alignItems: "center", width: "fit-content" },
  
  /* ✅ ADDED STYLING FOR THE FLEXIBLE BADGE */
  flexibleBadge: { fontSize: "11px", color: "#d97706", backgroundColor: "#fffbeb", padding: "4px 8px", borderRadius: "8px", border: "1px solid #fef3c7", display: "flex", gap: "4px", alignItems: "center", marginTop: "2px", width: "fit-content" },
  
  actionGroup: { display: "flex", gap: "10px", alignItems: "center" },
  commWrapper: { display: "flex", gap: "8px" },
  btn: { padding: "8px 16px", fontSize: "13px", borderRadius: "10px", border: "none", fontWeight: "700", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px" },
  acceptBtn: { backgroundColor: "#05a357", color: "#fff" },
  rejectBtn: { backgroundColor: "#fff", color: "#e11900", border: "1px solid #ffe5e5" },
  chatBtn: { backgroundColor: "#eef4ff", color: "#276ef1" },
  callBtn: { backgroundColor: "#eafff1", color: "#05a357" },

  /* Status Labels */
  statusLabel: { fontSize: "12px", fontWeight: "800", textTransform: "uppercase", padding: "6px 12px", borderRadius: "8px" },
  cancelledLabel: { color: "#e11d48", backgroundColor: "transparent" },
  rejectedLabel: { color: "#94a3b8", backgroundColor: "#f1f5f9" },
};